/**
 * @license
 * @copyright Copyright Motili Inc., 2017 All Rights Reserved
 */

import assign from 'lodash/assign';
import merge from 'lodash/merge';
import some from 'lodash/some';
import trim from 'lodash/trim';

import moment from 'moment';
import {
    isMotiliFinanceDept,
    hasRole,
} from '../../common/services/AuthService';
import * as ApiService from '../../common/services/ApiService';
import * as ContractorService from '../../common/services/ContractorService';
import {
    getCompanyAddresses,
    getClientDispatchingConfig,
} from './AdminClientsService';
import { createLoopbackFilter } from '../../common/components/data-grid/DataGridUtils';

export function getContractors(filterQuery) {
    const filterFields = {
        filter: {
            include: [
                {
                    relation: 'address',
                    scope: {
                        fields: [
                            'stdAddress',
                            'stdDeliveryLine',
                            'stdCity',
                            'stdRegion',
                            'stdPostalCode',
                            'stdCountry',
                            'standardized',
                            'timezone',
                            'appliedContractorLaborRate',
                        ],
                    },
                },
            ],
        },
    };
    const query = merge({}, filterFields, { filter: filterQuery });
    return ContractorService.find(query);
}

export function getCountOfContractors(query) {
    return ContractorService.count(query);
}

export function getDispatchableContractorAndTeamsForClient(
    clientId,
    contractorIds = null
) {
    const params = { clientId };
    if (contractorIds && contractorIds.length > 0) {
        params.contractorIds = contractorIds;
    }
    return ApiService.get(true, 'Contractors/dispatchableList', params);
}

function resolvePreferredContractorId(p) {
    return (
        p.contractor?.id ??
        p.contractor?.contractorId ??
        p.contractor?.companyId ??
        p.contractorId ??
        p.contractor_id ??
        p.preferredContractorId ??
        p.preferred_contractor_id
    );
}

export async function fetchContractorsForManualAssign(clientId) {
    const clientConfig = await getClientDispatchingConfig(clientId);
    const enabledExclusiveContractorDispatching =
        clientConfig?.enabledExclusiveContractorDispatching;

    if (!enabledExclusiveContractorDispatching) {
        return getDispatchableContractorAndTeamsForClient(clientId);
    }

    const preferredIds = (clientConfig?.preferredContractor ?? [])
        .map(resolvePreferredContractorId)
        .filter(Boolean);

    if (preferredIds.length === 0) {
        return [];
    }

    return getDispatchableContractorAndTeamsForClient(clientId, preferredIds);
}

export function getApprovedContractorList(where) {
    const { operationInCountry, ...restWhere } = where || {};
    return ApiService.get(true, 'Contractors', {
        filter: {
            where: {
                ...restWhere,
                ...(operationInCountry && operationInCountry !== 'ALL'
                    ? { operationInCountry }
                    : {}),
                statusId: { inq: ['APPROVED', 'ENABLED'] },
                dispatchable: true,
            },
            order: ['name ASC'],
            fields: [
                'id',
                'name',
                'doingBusinessAs',
                'statusId',
                'taxId',
                'operationInCountry',
                'enabledDynamicDispatching',
            ],
            include: [
                {
                    relation: 'teamMembers',
                    scope: {
                        include: [
                            {
                                relation: 'user',
                                scope: {
                                    fields: [
                                        'firstName',
                                        'lastName',
                                        'id',
                                        'active',
                                    ],
                                },
                            },
                            {
                                relation: 'credentials',
                                scope: {
                                    where: { active: true },
                                },
                            },
                        ],
                    },
                },
            ],
        },
    });
}

function phoneTermFromSearchOperator(term, filter) {
    const value = filter.searchTerm.value.replace(/\D/g, '');
    const cleanedPhone = term.replace(/\D/g, '');
    switch (filter.operator.id) {
        case 'startsWith':
            return cleanedPhone.startsWith(value);
        case 'contains':
            return cleanedPhone.includes(value);
        case 'endsWith':
            return cleanedPhone.endsWith(value);
        case 'equals':
            return cleanedPhone === value;
        case 'isEmpty':
            return false;
        default:
            return false;
    }
}

export async function processFilters(activeFilters, activeQuery) {
    const query = merge({}, activeQuery, {
        where: {
            and: [],
        },
    });

    if (!activeFilters.length) {
        return query;
    }

    const statusFilter = { or: [] };
    const tradeTypeFilter = { or: [] };
    const stdRegions = [];
    const stdCountrys = [];
    const phones = [];

    activeFilters.forEach(filter => {
        switch (filter.target) {
            // Search Filters
            case 'ADDRESS':
                query.where.and.push({
                    addressId: { inq: filter.term },
                });
                break;
            case 'CITY':
                query.where.and.push({ addressId: { inq: filter.term } });
                break;
            case 'CONTRACTOR':
                query.where.and.push({ id: { inq: filter.term } });
                break;
            case 'DOINGBUSINESSAS':
                query.where.and.push(createLoopbackFilter(filter).where);
                break;
            case 'PHONE':
                phones.push(filter);
                break;
            case 'ZIP':
                query.where.and.push({ addressId: { inq: filter.term } });
                break;
            case 'STATE':
                query.where.and.push({ addressId: { inq: filter.term } });
                break;
            // Advanced Filters
            case 'status':
                statusFilter.or.push({ statusId: filter.term });
                break;
            case 'tradeType':
                tradeTypeFilter.or.push({ id: { inq: filter.term } });
                break;
            case 'state':
                stdRegions.push(filter.term);
                break;
            case 'country':
                stdCountrys.push(filter.term);
                break;
            case 'zip':
                query.where.and.push({ addressId: { inq: filter.term } });
                break;
            case 'activeBefore':
                query.where.and.push({
                    approvalDate: {
                        lt: moment(filter.term)
                            .endOf('day')
                            .subtract(1, 'days'),
                    },
                });
                break;
            case 'activeAfter':
                query.where.and.push({
                    approvalDate: {
                        gt: moment(filter.term)
                            .endOf('day')
                            .subtract(1, 'days'),
                    },
                });
                break;
            default:
        }
    });

    if (phones.length > 0) {
        const contractors = await getContractors({
            fields: ['id', 'phoneContacts'],
        });
        if (contractors.length > 0) {
            const ids = contractors
                .filter(c =>
                    some(c.phoneContacts, phone =>
                        some(phones, filter =>
                            phoneTermFromSearchOperator(
                                trim(phone.phoneNumber),
                                filter
                            )
                        )
                    )
                )
                .map(c => c.id);
            query.where.and.push({ id: ids.length ? { inq: ids } : null });
        }
    }
    if (stdRegions.length > 0) {
        const companyAddresses = await getCompanyAddresses({
            fields: 'id',
            where: { stdRegion: { inq: stdRegions } },
        });
        query.where.and.push({
            addressId: { inq: companyAddresses.map(a => a.id) },
        });
    }

    if (stdCountrys.length > 0) {
        const companyAddresses = await getCompanyAddresses({
            fields: 'id',
            where: { stdCountry: { inq: stdCountrys } },
        });
        query.where.and.push({
            addressId: { inq: companyAddresses.map(a => a.id) },
        });
    }

    query.where.and.push(statusFilter, tradeTypeFilter);

    return query;
}

export function getContractor(id, explicitQuery) {
    const defaultQuery = {
        include: [
            {
                relation: 'teamMembers',
                scope: {
                    fields: [
                        'id',
                        'userId',
                        'afterHoursSupport',
                        'receivesNotifications',
                        'createdAt',
                        'restrictToAssigned',
                        'externalCertifications',
                        'internalCertifications',
                        'militaryBaseAccess',
                        'assetTypes',
                        'trades',
                        'serviceTypes',
                        'titleId',
                        'reassignWorkOrder',
                        'lastJobCompletedAt',
                    ],
                    include: [
                        {
                            relation: 'user',
                            scope: {
                                fields: [
                                    'id',
                                    'active',
                                    'firstName',
                                    'lastName',
                                    'email',
                                    'timezoneId',
                                    'lastLogin',
                                ],
                                include: ['roles', 'preferences'],
                            },
                        },
                        {
                            relation: 'credentials',
                            scope: {
                                where: { active: true },
                            },
                        },
                    ],
                },
            },
            {
                relation: 'address',
                scope: {
                    fields: [
                        'rawAddress',
                        'latitude',
                        'longitude',
                        'stdDeliveryLine',
                        'stdCity',
                        'stdRegion',
                        'stdPostalCode',
                        'stdPostalCodePlus4',
                        'stdAddress',
                        'stdCountry',
                        'standardized',
                        'timezone',
                    ],
                },
            },
            {
                relation: 'mailingAddress',
                scope: {
                    fields: [
                        'rawAddress',
                        'latitude',
                        'longitude',
                        'stdDeliveryLine',
                        'stdCity',
                        'stdRegion',
                        'stdPostalCode',
                        'stdPostalCodePlus4',
                        'stdAddress',
                        'stdCountry',
                        'standardized',
                        'timezone',
                    ],
                },
            },
            {
                relation: 'paymentAddress',
                scope: {
                    fields: [
                        'rawAddress',
                        'stdDeliveryLine',
                        'stdCity',
                        'stdRegion',
                        'stdPostalCode',
                        'stdAddress',
                        'stdCountry',
                        'standardized',
                        'timezone',
                    ],
                },
            },
            {
                relation: 'tradeTypes',
            },
            {
                relation: 'ratings',
            },
            {
                relation: 'serviceAreas',
            },
            {
                relation: 'files',
            },
            {
                relation: 'favoriteDistributionCenterMappings',
            },
            {
                relation: 'favoriteDistributionCenters',
                scope: {
                    include: ['vendor'],
                },
            },
            {
                relation: 'qualifications',
                scope: {
                    include: ['file'],
                },
            },
            {
                relation: 'capacity',
            },
            {
                relation: 'assetTypes',
            },
            {
                relation: 'serviceTypes',
            },
            {
                relation: 'notes',
            },
            {
                relation: 'directClients',
            },
            {
                relation: 'contractorDispatchRule',
            },
            {
                relation: 'directRelationRecords',
            },
            {
                relation: 'laborRates',
            },
            {
                relation: 'blacklists',
                scope: {
                    include: {
                        relation: 'client',
                        scope: {
                            fields: ['name'],
                        },
                    },
                    fields: ['reasonId', 'clientId', 'id'],
                },
            },
            {
                relation: 'preferredLists',
                scope: {
                    include: {
                        relation: 'client',
                        scope: {
                            fields: [
                                'name',
                                'enabledExclusiveContractorDispatching',
                            ],
                        },
                    },
                    fields: ['clientId', 'id'],
                },
            },
            { relation: 'clientPaymentAccount' },
        ],
        fields: [
            'id',
            'statusId',
            'dispatchable',
            'directSupply',
            'asureCertified',
            'asureCertifiedNumber',
            'enabledDynamicDispatching',
            'ffScheduler',
            'eCommEnabled',
            'name',
            'doingBusinessAs',
            'website',
            'phoneContacts',
            'comments',
            'contact',
            'approvalDate',
            'created',
            'updated',
            'addressId',
            'taxId',
            'paymentAddressId',
            'paymentMethodId',
            'corpTypeId',
            'crossesStateLines',
            'serviceRadius',
            'termNet',
            'mailingAddressId',
            'taxId',
            'hoursOfOperation',
            'emergencyHours',
            'accountOwnerId',
            'secondaryAccountOwnerId',
            'operationInCountry',
            'clientPaymentAccountId',
            'selfOrdering',
            'enableSingleSession',
            'enableProductSearchList',
        ],
    };

    const query = { filter: explicitQuery || defaultQuery };
    return ContractorService.findById(id, query);
}

export function findContractorTradeType(query) {
    return ApiService.get(true, 'Contractors/findContractorTradeType', {
        filter: query,
    });
}

/**
 *
 * @param {number} id
 * @param {object} note
 * @param {number} note.accountId
 * @param {number} note.contractorId
 * @param {string} note.fullName
 * @param {number} note.id
 * @param {string} note.message
 * @param {string} note.messageType
 * @param {string} note.created
 * @param {string} note.updated
 *
 * @returns {Promise}
 */
export function createNote(id, note) {
    return ContractorService.createNote(id, note);
}

/**
 *
 * @param {number} id
 * @param {object} rating
 * @param {string} note
 * @param {object} mentionsData
 */
export function createRating(id, rating, note, mentionsData) {
    const _rating = assign(
        {},
        {
            rating,
            feedback: note,
            typeId: 'INTERNAL',
            mentionsData,
        }
    );
    return ContractorService.createRating(id, _rating);
}

export function updateNote(id, note) {
    return ContractorService.updateNote(id, note.id, note);
}

export function updateCapacity(id, capacity) {
    return ApiService.put(true, `Contractors/${id}/capacity`, capacity);
}

export function activateTeamMember(id, teamMemberId) {
    return ContractorService.activateTeamMember(id, teamMemberId).then(() =>
        getContractor(id)
    );
}

export function deactivateTeamMember(id, teamMemberId) {
    return ContractorService.deactivateTeamMember(id, teamMemberId).then(() =>
        getContractor(id)
    );
}

export function updateRestrictTeamMember(contractorId, id, value) {
    return ContractorService.updateRestrictTeamMember(
        contractorId,
        id,
        value
    ).then(() => getContractor(contractorId));
}

export function createTeamMember(id, teamMember) {
    return ContractorService.createTeamMember(id, teamMember).then(() =>
        getContractor(id)
    );
}

export function updateTeamMember(contractorId, memberId, update) {
    return ContractorService.updateTeamMember(
        contractorId,
        memberId,
        update
    ).then(() => getContractor(contractorId));
}

export function getCompanyAddressesByZip(query) {
    return ApiService.get(true, 'CompanyAddresses/findDistZip', {
        filter: query,
    });
}

export function getCompanyAddressById(id) {
    return ApiService.get(true, `CompanyAddresses/${id}`);
}

/**
 * @param {Number} contractorId
 * @param {String} data
 * @param {String} [description]
 * @param {String} url
 * @param {Object} file
 * @param {String} file.name
 * @param {String} file.type
 * @param {Number} file.size
 * @param {Object} result
 *
 * @returns {Promise}
 */
export function uploadQualification(contractorId, data) {
    return ContractorService.uploadQualification(contractorId, data);
}

export function uploadFile(data) {
    return ContractorService.uploadFile(data);
}

export function deleteFile(fileId) {
    return ContractorService.deleteFile(fileId);
}

export function deleteQualification(contractorId, qualificationId, fileId) {
    return ContractorService.deleteQualification(
        contractorId,
        qualificationId,
        fileId
    );
}

export function addFavoriteDistributionCenter(
    contractorId,
    distributionCenterId
) {
    return ContractorService.addFavoriteDistributionCenter(
        contractorId,
        distributionCenterId
    );
}

export function removeFavoriteDistributionCenter(
    contractorId,
    favoriteDistributionCenterFk
) {
    return ContractorService.removeFavoriteDistributionCenter(
        contractorId,
        favoriteDistributionCenterFk
    );
}

export function approve(contractorId, reason) {
    const data = {
        id: contractorId,
        reason,
        statusId: 'APPROVED',
    };
    return ContractorService.updateStatus(data);
}

export function deactivate(contractorId, reason) {
    const data = {
        id: contractorId,
        reason,
        statusId: 'INACTIVE',
    };
    return ContractorService.updateStatus(data);
}

export function enableTemporarily(contractorId, reason) {
    const data = {
        id: contractorId,
        reason,
        statusId: 'ENABLED',
    };
    return ContractorService.updateStatus(data);
}

export function pendingApproval(contractorId, reason) {
    const data = {
        id: contractorId,
        reason,
        statusId: 'PENDING_APPROVAL',
    };
    return ContractorService.updateStatus(data);
}

export function shouldDisplayActionApprove(contractor) {
    const acceptableRoles = ['motili_owner', 'motili_admin'];
    if (!hasRole(acceptableRoles)) {
        return false;
    }
    if (!contractor) {
        return false;
    }
    const statusId = contractor.statusId;
    if (
        statusId === 'INACTIVE' ||
        statusId === 'PENDING_APPROVAL' ||
        statusId === 'ENABLED'
    ) {
        return isMotiliFinanceDept();
    }
    return false;
}

export function shouldDisplayActionDeactivate(contractor) {
    if (!contractor) {
        return false;
    }
    const statusId = contractor.statusId;
    if (
        statusId === 'APPROVED' ||
        statusId === 'ENABLED' ||
        statusId === 'INSURANCE_EXPIRED' ||
        statusId === 'PENDING_APPROVAL'
    ) {
        const acceptableRoles = ['motili_owner', 'motili_admin'];
        if (hasRole(acceptableRoles)) {
            return true;
        }
    }
    return false;
}

export function shouldDisplayActionEnableTemporarily(contractor) {
    const acceptableRoles = ['motili_owner', 'motili_admin'];
    if (!hasRole(acceptableRoles)) {
        return false;
    }
    if (!contractor) {
        return false;
    }
    const statusId = contractor.statusId;
    if (statusId === 'INACTIVE' || statusId === 'PENDING_APPROVAL') {
        return isMotiliFinanceDept();
    }
    return false;
}

export function shouldDisplayActionPendingApproval(contractor) {
    if (!contractor) {
        return false;
    }
    const statusId = contractor.statusId;
    if (statusId === 'ACCEPTED') {
        const acceptableRoles = ['motili_owner', 'motili_admin'];
        if (hasRole(acceptableRoles)) {
            return true;
        }
    }
    return false;
}

export function create(contractor) {
    return ContractorService.create(contractor);
}

export function updateServiceTypes(
    contractor,
    selectedServiceTypes,
    selectedAssetTypes
) {
    // find added service types
    const addedServiceTypes = selectedServiceTypes.filter(
        type =>
            !contractor.serviceTypes.some(
                item => item.serviceTypeId === type.id
            )
    );
    // find deleted service types
    const deletedServiceTypes = contractor.serviceTypes.filter(
        type =>
            !selectedServiceTypes.some(item => item.id === type.serviceTypeId)
    );
    // find added asset types
    const addedAssetTypes = selectedAssetTypes.filter(
        type => !contractor.assetTypes.some(item => item.id === type.id)
    );
    // find deleted asset types
    const deletedAssetTypes = contractor.assetTypes.filter(
        type => !selectedAssetTypes.some(item => item.id === type.id)
    );

    return Promise.all([
        ...addedAssetTypes.map(type =>
            ApiService.put(
                true,
                `Contractors/${contractor.id}/assetTypes/rel/${type.id}`
            )
        ),
        ...deletedAssetTypes.map(type =>
            ApiService.del(
                `Contractors/${contractor.id}/assetTypes/rel/${type.id}`
            )
        ),
        ...addedServiceTypes.map(type =>
            ApiService.post(true, `Contractors/${contractor.id}/serviceTypes`, {
                serviceTypeId: type.id,
            })
        ),
        ...deletedServiceTypes.map(type =>
            ApiService.del(
                `Contractors/${contractor.id}/serviceTypes/${type.id}`
            )
        ),
    ]);
}

export async function getContractorDispatchRule(contractorId) {
    return ContractorService.getContractorDispatchRule(contractorId);
}

export async function createContractorDispatchRule(contractorId, data) {
    return ContractorService.createContractorDispatchRule(contractorId, data);
}

export async function updateContractorDispatchRule(
    contractorId,
    ruleId,
    update
) {
    return ContractorService.updateContractorDispatchRule(
        contractorId,
        ruleId,
        update
    );
}

export async function getRuleTypes() {
    return ContractorService.getRuleTypes();
}

export function calculateDispatchingDisabled(dispatchRule) {
    return ContractorService.calculateDispatchingDisabled(dispatchRule);
}

export function getTeamCapacityStats(id) {
    return ContractorService.getTeamCapacityStats(id);
}
